/**
 * DocumentGrid Component
 * 
 * Grid layout for displaying documents as cards
 */

import React from 'react';
import { Document } from '../types/documentsTypes';
import { DocumentCard, DocumentCardSkeleton } from './DocumentCard';
import { useDocumentsStore } from '../stores/documentsStore';
import { FileX } from 'lucide-react';
import { cn } from '@/core/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface DocumentGridProps {
  documents: Document[];
  isLoading?: boolean;
  onDocumentClick?: (document: Document) => void;
  onDocumentAction?: (action: string, document: Document) => void;
  className?: string;
  emptyMessage?: string;
  emptyDescription?: string;
}

// ============================================================================
// Component
// ============================================================================

export const DocumentGrid: React.FC<DocumentGridProps> = ({
  documents,
  isLoading = false,
  onDocumentClick,
  onDocumentAction,
  className,
  emptyMessage = 'No documents found',
  emptyDescription = 'Upload documents or adjust your filters to see results.',
}) => {
  const { selectedDocumentIds, toggleDocumentSelection } = useDocumentsStore();

  const handleSelect = (documentId: string, selected: boolean) => {
    toggleDocumentSelection(documentId);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn(
        'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4',
        className
      )}>
        {Array.from({ length: 8 }).map((_, i) => (
          <DocumentCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Empty state
  if (documents.length === 0) {
    return (
      <div className={cn(
        'flex flex-col items-center justify-center py-16 px-4',
        className
      )}>
        <div className="w-16 h-16 rounded-full bg-cream-dark flex items-center justify-center mb-4">
          <FileX className="w-8 h-8 text-warm-gray" />
        </div>
        <h3 className="text-lg font-medium text-navy mb-2">{emptyMessage}</h3>
        <p className="text-warm-gray text-center max-w-md">{emptyDescription}</p>
      </div>
    );
  }

  return (
    <div className={cn(
      'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4',
      className
    )}>
      {documents.map((document) => (
        <DocumentCard
          key={document.id}
          document={document}
          isSelected={selectedDocumentIds.includes(document.id)}
          onSelect={handleSelect}
          onClick={() => onDocumentClick?.(document)}
          onAction={(action) => onDocumentAction?.(action, document)}
        />
      ))}
    </div>
  );
};

// ============================================================================
// Exports
// ============================================================================

export default DocumentGrid;


